// Resident Navigation Configuration
// Navigation items for the resident mobile menu matching Figma design

import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';

// Navigation group types
export type NavigationGroup = 'QUICK_ACTIONS' | 'COMMUNITY' | 'MY_ACCOUNT';

// Navigation group labels for section headers
export const NAVIGATION_GROUP_LABELS: Record<NavigationGroup, string> = {
  QUICK_ACTIONS: 'Quick Actions',
  COMMUNITY: 'Community',
  MY_ACCOUNT: 'My Account',
};

// Navigation group order for consistent rendering
export const NAVIGATION_GROUP_ORDER: NavigationGroup[] = [
  'QUICK_ACTIONS',
  'COMMUNITY',
  'MY_ACCOUNT',
];

// Navigation item type for resident mobile menu
export interface IResidentNavigationItem {
  id: string;
  path: string;
  title: string;
  iconKey: keyof typeof RESIDENTS_SVG_COMPONENTS;
  group: NavigationGroup;
  // Optional condition function to show/hide based on user state
  condition?: (user: IResidentUserState) => boolean;
}

// User state for conditional navigation
export interface IResidentUserState {
  customerType?: string;
  isWifiEnabled?: boolean;
}

// Screen name mapping for navigation (new Figma-based navigator)
export const RESIDENT_PATH_TO_SCREEN: Record<string, string> = {
  '/home': 'ResidentHome',
  '/payments': 'ResidentPayments',
  '/linked-accounts': 'ResidentLinkedAccounts',
  '/maintenance': 'ResidentMaintenance',
  '/events': 'ResidentEvents',
  '/marketplace': 'ResidentMarketplace',
  '/newsfeed': 'ResidentCommunityBoard',
  '/resources': 'ResidentResources',
  '/wifi': 'WifiConnection',
  '/faq': 'ResidentFaq',
  '/property-chat': 'ResidentMessages',
  '/notifications': 'ResidentNotifications',
  '/my-account': 'ResidentProfile',
  '/account-settings': 'ResidentAccountSettings',
};

// Legacy screen name mapping (for compatibility with old navigation)
export const RESIDENT_PATH_TO_LEGACY_SCREEN: Record<string, string> = {
  '/home': 'ResidentHome',
  '/payments': 'Payments',
  '/linked-accounts': 'LinkedAccounts',
  '/maintenance': 'Maintenance',
  '/events': 'EventDetail',
  '/wifi': 'WifiConnection',
  '/faq': 'ResidentFAQ',
  '/property-chat': 'Messages',
  '/notifications': 'BulletinsList',
  '/my-account': 'Profile',
};

// Navigation items organized by groups
export const RESIDENT_NAVIGATION_ITEMS: IResidentNavigationItem[] = [
  // QUICK ACTIONS
  {
    id: 'home',
    path: '/home',
    title: 'Home',
    iconKey: 'HOUSE',
    group: 'QUICK_ACTIONS',
  },
  {
    id: 'payments',
    path: '/payments',
    title: 'Payments',
    iconKey: 'CREDIT_CARD',
    group: 'QUICK_ACTIONS',
    // Hide for Non-Leasing Occupants
    condition: (user) => user.customerType !== 'non-leasing occupant',
  },
  {
    id: 'maintenance',
    path: '/maintenance',
    title: 'Maintenance',
    iconKey: 'WRENCH',
    group: 'QUICK_ACTIONS',
  },
  {
    id: 'property-chat',
    path: '/property-chat',
    title: 'Property Chat',
    iconKey: 'CHAT_ICON',
    group: 'QUICK_ACTIONS',
  },
  // COMMUNITY
  {
    id: 'events',
    path: '/events',
    title: 'Events',
    iconKey: 'STAR',
    group: 'COMMUNITY',
  },
  {
    id: 'marketplace',
    path: '/marketplace',
    title: 'Marketplace',
    iconKey: 'SUPERMARKET',
    group: 'COMMUNITY',
  },
  {
    id: 'newsfeed',
    path: '/newsfeed',
    title: 'Newsfeed',
    iconKey: 'CHAT_ICON',
    group: 'COMMUNITY',
  },
  {
    id: 'notifications',
    path: '/notifications',
    title: 'Notifications',
    iconKey: 'NOTIFICATION',
    group: 'COMMUNITY',
  },
  // MY ACCOUNT
  {
    id: 'account-settings',
    path: '/account-settings',
    title: 'Settings',
    iconKey: 'USER',
    group: 'MY_ACCOUNT',
  },
  {
    id: 'resources',
    path: '/resources',
    title: 'Resources',
    iconKey: 'WIDGETS',
    group: 'MY_ACCOUNT',
  },
  {
    id: 'linked-accounts',
    path: '/linked-accounts',
    title: 'Linked Accounts',
    iconKey: 'LINKED_ACCOUNTS',
    group: 'MY_ACCOUNT',
  },
  {
    id: 'faq',
    path: '/faq',
    title: 'FAQ',
    iconKey: 'FAQ',
    group: 'MY_ACCOUNT',
  },
];

// Color constants for resident navigation (from Figma design)
export const RESIDENT_NAV_COLORS = {
  BACKGROUND: '#242424',
  TEXT_PRIMARY: '#EEF1F4',
  TEXT_SECONDARY: '#6B8EFE',
  TEXT_TERTIARY: '#919191',
  BORDER_PRIMARY: '#2F2F2F',
  BUTTON_BACKGROUND: '#EEF1F4',
  BUTTON_TEXT: '#171717',
  NOTIFICATION_KNOB: '#FF7629',
} as const;

// Helper function to get navigation items grouped by section
export const getGroupedNavigationItems = (
  userState: IResidentUserState = {}
): Record<NavigationGroup, IResidentNavigationItem[]> => {
  const filteredItems = RESIDENT_NAVIGATION_ITEMS.filter((item) => {
    if (item.condition) {
      return item.condition(userState);
    }
    return true;
  });

  return NAVIGATION_GROUP_ORDER.reduce((acc, group) => {
    acc[group] = filteredItems.filter((item) => item.group === group);
    return acc;
  }, {} as Record<NavigationGroup, IResidentNavigationItem[]>);
};

